/** Сжатие картинок в браузере перед загрузкой в Storage. */

export const AVATAR_MAX_SIDE = 512;
export const PHOTO_MAX_SIDE = 1200;
export const DEFAULT_QUALITY = 0.8;
export const MIN_QUALITY = 0.6;
export const QUALITY_STEP = 0.1;
/** Потолок веса после сжатия: выше — понижаем качество шагами до MIN_QUALITY. */
export const MAX_OUTPUT_BYTES = 400 * 1024;
export const SKIP_BYTES = 150 * 1024;
/** Год — только для уникальных путей (timestamp/uuid в имени). */
export const LONG_CACHE = "31536000";

export type CompressOptions = { maxSide: number; quality?: number };

/** Типы, которые не трогаем: анимированные GIF и векторные SVG. */
export function isPassthroughType(type: string, name = ""): boolean {
  const t = type.toLowerCase();
  const n = name.toLowerCase();
  return t === "image/gif" || t === "image/svg+xml" || n.endsWith(".gif") || n.endsWith(".svg");
}

/** Целевой размер: длинная сторона ≤ maxSide, пропорции сохраняются, без увеличения. */
export function targetSize(width: number, height: number, maxSide: number): { width: number; height: number } {
  if (width <= 0 || height <= 0) return { width, height };
  const scale = Math.min(1, maxSide / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Можно ли вернуть файл как есть: мал по весу и по размеру. */
export function canSkip(bytes: number, width: number, height: number, maxSide: number): boolean {
  return bytes <= SKIP_BYTES && Math.max(width, height) <= maxSide;
}

export function jpegName(name: string): string {
  return renameExt(name, "jpg");
}

export function renameExt(name: string, ext: "jpg" | "webp"): string {
  const base = name.replace(/\.[^./\\]+$/, "") || "photo";
  return `${base}.${ext}`;
}

/** Последовательность качеств: от стартового вниз шагами, не ниже MIN_QUALITY. */
export function qualitySteps(start: number): number[] {
  const out: number[] = [];
  for (let q = start; q >= MIN_QUALITY - 1e-9; q -= QUALITY_STEP) out.push(Math.round(q * 100) / 100);
  return out.length ? out : [MIN_QUALITY];
}

/** Расширение для пути в Storage по фактическому файлу. */
export function fileExt(file: File): string {
  if (file.type === "image/jpeg") return "jpg";
  if (file.type === "image/webp") return "webp";
  const fromName = file.name.split(".").pop();
  if (fromName && fromName !== file.name) return fromName.toLowerCase();
  const fromType = file.type.split("/")[1];
  return fromType ? fromType.replace("svg+xml", "svg") : "jpg";
}

async function decode(file: File): Promise<{ source: CanvasImageSource; width: number; height: number; close: () => void }> {
  if (typeof createImageBitmap === "function") {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
      return { source: bmp, width: bmp.width, height: bmp.height, close: () => bmp.close() };
    } catch {
      /* фолбэк на <img loading="lazy" decoding="async"> ниже */
    }
  }
  // <img loading="lazy" decoding="async"> в современных браузерах сам учитывает EXIF (image-orientation: from-image).
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    await img.decode();
    return { source: img, width: img.naturalWidth, height: img.naturalHeight, close: () => URL.revokeObjectURL(url) };
  } catch (e) {
    URL.revokeObjectURL(url);
    throw e;
  }
}

/**
 * Уменьшает картинку до maxSide по длинной стороне и кодирует в WebP (без поддержки — JPEG).
 * Тяжелее MAX_OUTPUT_BYTES — понижаем качество до MIN_QUALITY. При ошибке — исходный файл.
 */
export async function compressImage(file: File, { maxSide, quality = DEFAULT_QUALITY }: CompressOptions): Promise<File> {
  try {
    if (typeof document === "undefined") return file;
    if (!file.type.startsWith("image/") || isPassthroughType(file.type, file.name)) return file;
    const img = await decode(file);
    try {
      if (canSkip(file.size, img.width, img.height, maxSide)) return file;
      const { width, height } = targetSize(img.width, img.height, maxSide);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return file;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img.source, 0, 0, width, height);
      const encode = (type: string, q: number) =>
        new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, q));
      let type = "image/webp";
      let blob = await encode(type, quality);
      if (!blob || blob.type !== "image/webp") {
        // Без WebP — JPEG на белом фоне (у JPEG нет прозрачности).
        type = "image/jpeg";
        ctx.globalCompositeOperation = "destination-over";
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
        ctx.globalCompositeOperation = "source-over";
        blob = await encode(type, quality);
        if (!blob || blob.type !== "image/jpeg") return file;
      }
      for (const q of qualitySteps(quality).slice(1)) {
        if (blob.size <= MAX_OUTPUT_BYTES) break;
        const next = await encode(type, q);
        if (next) blob = next;
      }
      const resized = Math.max(img.width, img.height) > maxSide;
      if (blob.size >= file.size && !resized) return file;
      const ext = type === "image/webp" ? "webp" : "jpg";
      return new File([blob], renameExt(file.name, ext), { type, lastModified: Date.now() });
    } finally {
      img.close();
    }
  } catch {
    return file;
  }
}

/** До сжатия отсекаем только совсем огромные файлы; основной лимит — после compressImage. */
export const RAW_MAX_BYTES = 25 * 1024 * 1024;
export const MB = 1024 * 1024;
export const tooLargeRaw = (size: number) => size > RAW_MAX_BYTES;
export const tooLargeCompressed = (size: number, limitMb: number) => size > limitMb * MB;
